<template>
    <eSesjaModal>
        <eSesjaModalHeader @close="closeModal">
            {{ $t("com.global.edit") }} {{ $t("com.discussion.profile.modes") }}
        </eSesjaModalHeader>
        <eSesjaModalBody style="min-width: 600px;">
            <section class="discussion-profile-mode-form" v-if="editingMode && availableTypesForEdit.length">
                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-type") }}</label>
                    <select v-model.number="editingMode.typeId" class="edit-point-form__input"
                        @change="syncEditedTypeName">
                        <option v-for="type in availableTypesForEdit" :key="type.id" :value="type.id">
                            {{ type.name }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-limit-group") }}</label>
                    <select v-model="editingMode.limitGroupId" class="edit-point-form__input"
                        :disabled="isLimitGroupDisabled">
                        <option :value="currentLimitGroupValue">{{
                            $t("com.discussion.profile.mode-limit-group.no-group") }}</option>
                        <option v-for="option in availableLimitGroupModesForEdit" :key="`edit-limit-group-${option.id}`"
                            :value="option.id">
                            {{ option.type || "-" }}
                        </option>
                    </select>
                </section>

                <template v-if="!isGroupedLimitSelected">
                    <section class="discussion-profile-mode-form__row">
                        <section class="discussion-profile-mode-form__switch-row">
                            <InputSwitch id="edit-mode-block-modal" v-model="editingMode.isBlock" />
                            <label class="edit-point-form__label discussion-profile-mode-form__switch-label"
                                for="edit-mode-block-modal">
                                {{ $t("com.discussion.profile.mode-block") }}
                            </label>
                        </section>
                    </section>
                    <section class="discussion-profile-mode-form__row">
                        <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-limit-time-type")
                            }}</label>
                        <section class="discussion-profile-mode-form__type-groups">
                            <section class="discussion-profile-mode-form__type-group"
                                :class="{ 'discussion-profile-mode-form__type-group--disabled': isStandardStatementDisabled }">
                                <section class="discussion-profile-mode-form__switch-row">
                                    <InputSwitch id="edit-limit-time-type-1"
                                        :model-value="editingMode.limitTimeType === 1"
                                        @update:modelValue="setLimitTimeType(1, $event)" />
                                    <label class="edit-point-form__label discussion-profile-mode-form__switch-label"
                                        for="edit-limit-time-type-1">
                                        {{ $t("com.discussion.profile.mode-limit-time-type.fixed") }}
                                    </label>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{
                                        $t("com.discussion.profile.mode-first-statement") }}</label>
                                    <select v-model.number="editingMode.firstStatement" class="edit-point-form__input"
                                        :disabled="isStandardStatementDisabled">
                                        <option v-for="option in statementTimeOptions" :key="`edit-first-${option.id}`"
                                            :value="option.id">
                                            {{ $t(option.textKey) }}
                                        </option>
                                    </select>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{
                                        $t("com.discussion.profile.mode-second-statement") }}</label>
                                    <select v-model.number="editingMode.secondStatement" class="edit-point-form__input"
                                        :disabled="isStandardStatementDisabled">
                                        <option v-for="option in statementTimeOptions" :key="`edit-second-${option.id}`"
                                            :value="option.id">
                                            {{ $t(option.textKey) }}
                                        </option>
                                    </select>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{
                                        $t("com.discussion.profile.mode-third-statement") }}</label>
                                    <select v-model.number="editingMode.thirdStatement" class="edit-point-form__input"
                                        :disabled="isStandardStatementDisabled">
                                        <option v-for="option in statementTimeOptions" :key="`edit-third-${option.id}`"
                                            :value="option.id">
                                            {{ $t(option.textKey) }}
                                        </option>
                                    </select>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{
                                        $t("com.discussion.profile.mode-next-statement") }}</label>
                                    <select v-model.number="editingMode.nextStatement" class="edit-point-form__input"
                                        :disabled="isStandardStatementDisabled">
                                        <option v-for="option in statementTimeOptions" :key="`edit-next-${option.id}`"
                                            :value="option.id">
                                            {{ $t(option.textKey) }}
                                        </option>
                                    </select>
                                </section>
                            </section>

                            <section class="discussion-profile-mode-form__type-group"
                                :class="{ 'discussion-profile-mode-form__type-group--disabled': isElasticStatementDisabled && isRequestLimitDisabled }">
                                <section class="discussion-profile-mode-form__switch-row">
                                    <InputSwitch id="edit-limit-time-type-2"
                                        :model-value="editingMode.limitTimeType === 2"
                                        @update:modelValue="setLimitTimeType(2, $event)" />
                                    <label class="edit-point-form__label discussion-profile-mode-form__switch-label"
                                        for="edit-limit-time-type-2">
                                        {{ $t("com.discussion.profile.mode-limit-time-type.flex") }}
                                    </label>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{
                                        $t("com.discussion.profile.mode-elastic-statement") }}</label>
                                    <select v-model.number="editingMode.elasticStatement" class="edit-point-form__input"
                                        :disabled="isElasticStatementDisabled" @change="handleElasticStatementChange">
                                        <option v-for="option in statementTimeOptions"
                                            :key="`edit-elastic-${option.id}`" :value="option.id">
                                            {{ $t(option.textKey) }}
                                        </option>
                                    </select>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{
                                        $t("com.discussion.profile.mode-request-limit") }}</label>
                                    <select v-model="editingMode.requestLimitId" class="edit-point-form__input"
                                        :disabled="isRequestLimitDisabled" @change="handleRequestLimitChange">
                                        <option v-for="option in requestLimitTypes"
                                            :key="`edit-request-limit-${option.id}`" :value="option.id">
                                            {{ $t(option.name) }}
                                        </option>
                                    </select>
                                </section>
                            </section>
                        </section>
                    </section>

                    <section class="discussion-profile-mode-form__row">
                        <section class="discussion-profile-mode-form__switch-row">
                            <InputSwitch id="edit-mode-turn-off-modal" v-model="editingMode.turnOff"
                                :disabled="isTurnOffDisabled" />
                            <label class="edit-point-form__label discussion-profile-mode-form__switch-label"
                                for="edit-mode-turn-off-modal">
                                {{ $t("com.discussion.profile.mode-turn-off") }}
                            </label>
                        </section>
                    </section>
                    <section class="discussion-profile-mode-form__row">
                        <section class="discussion-profile-mode-form__switch-row">
                            <InputSwitch id="edit-mode-sound-modal" v-model="editingMode.isSoundNotification"
                                :disabled="isSoundNotificationDisabled" />
                            <label class="edit-point-form__label discussion-profile-mode-form__switch-label"
                                for="edit-mode-sound-modal">
                                {{ $t("com.discussion.profile.mode-sound-notification") }}
                            </label>
                        </section>
                    </section>
                </template>
                <section v-else class="discussion-profile-mode-form__group-message">
                    {{ $t("com.discussion.profile.mode-limit-group-inherited-info") }}
                </section>
            </section>
            <section v-else class="text-center">
                {{ $t("com.global.no-data") }}
            </section>
        </eSesjaModalBody>
        <eSesjaModalFooter>
            <eSesjaModalButtonCancel @close="closeModal">
                {{ $t("com.global.cancel") }}
            </eSesjaModalButtonCancel>
            <eSesjaModalButtonSave :disabled="isSaveDisabled" @save="saveMode">
                {{ $t("com.global.save") }}
            </eSesjaModalButtonSave>
        </eSesjaModalFooter>
    </eSesjaModal>
</template>

<script charset="utf-8">
import AlertHelper from "@/helpers/alerts.helper.js";
import DiscussionProfileService from "@/services/discussion.profile.service";
import InputSwitch from "@/components/BasicControls/InputSwitch.vue";

export default {
    name: "EditDiscussionProfileModeModal",
    components: {
        InputSwitch
    },
    props: {
        discussionId: {
            type: Number,
            required: true
        },
        mode: {
            type: Object,
            required: true
        },
        statementTypes: {
            type: Array,
            default: () => []
        },
        statementTimeOptions: {
            type: Array,
            default: () => []
        },
        limitTimeTypeOptions: {
            type: Array,
            default: () => []
        },
        requestLimitTypes: {
            type: Array,
            default: () => []
        },
        availableLimitGroupModes: {
            type: Array,
            default: () => []
        },
        usedTypeIds: {
            type: Array,
            default: () => []
        }
    },
    emits: ["close", "updated"],
    data() {
        return {
            isSaving: false,
            editingMode: null
        };
    },
    computed: {
        availableTypesForEdit() {
            return this.statementTypes.filter((type) => {
                if (type.id === this.editingMode?.typeId) {
                    return true;
                }

                return !this.usedTypeIds.includes(type.id);
            });
        },
        availableLimitGroupModesForEdit() {
            return this.availableLimitGroupModes.filter((mode) => {
                const modeId = Number(mode?.id);
                const limitGroupId = this.normalizeNullableNumber(mode?.limitGroupId);

                return modeId > 0
                    && modeId !== Number(this.editingMode?.id)
                    && limitGroupId === modeId;
            });
        },
        currentLimitGroupValue() {
            return this.editingMode?.id ?? null;
        },
        defaultElasticStatement() {
            return -1;
        },
        defaultRequestLimitId() {
            return this.requestLimitTypes.length > 0 ? Number(this.requestLimitTypes[0].id) : 1;
        },
        hasChildModes() {
            const editingModeId = Number(this.editingMode?.id);

            if (!editingModeId) {
                return false;
            }

            return this.availableLimitGroupModes.some((mode) => {
                return Number(mode?.id) !== editingModeId
                    && this.normalizeNullableNumber(mode?.limitGroupId) === editingModeId;
            });
        },
        isLimitGroupDisabled() {
            return this.editingMode?.isBlock || this.hasChildModes;
        },
        isGroupedLimitSelected() {
            return this.editingMode && this.editingMode.limitGroupId !== null && Number(this.editingMode.limitGroupId) !== Number(this.currentLimitGroupValue);
        },
        hasCustomElasticStatement() {
            return !!this.editingMode && this.normalizeNumber(this.editingMode.elasticStatement, this.defaultElasticStatement) !== this.defaultElasticStatement;
        },
        hasCustomRequestLimit() {
            return !!this.editingMode && this.normalizeNumber(this.editingMode.requestLimitId, this.defaultRequestLimitId) !== this.defaultRequestLimitId;
        },
        hasActiveLimitTimeType() {
            return this.isFixedLimitTimeTypeActive || this.isFlexLimitTimeTypeActive;
        },
        isFixedLimitTimeTypeActive() {
            return Number(this.editingMode?.limitTimeType) === 1;
        },
        isFlexLimitTimeTypeActive() {
            return Number(this.editingMode?.limitTimeType) === 2;
        },
        isLimitTimeTypeInteractionDisabled() {
            return this.editingMode?.isBlock || this.isGroupedLimitSelected;
        },
        isFixedLimitTimeTypeSwitchDisabled() {
            return this.isLimitTimeTypeInteractionDisabled || this.isFlexLimitTimeTypeActive;
        },
        isFlexLimitTimeTypeSwitchDisabled() {
            return this.isLimitTimeTypeInteractionDisabled || this.isFixedLimitTimeTypeActive;
        },
        isStandardStatementDisabled() {
            return this.editingMode?.isBlock || this.isGroupedLimitSelected || !this.isFixedLimitTimeTypeActive;
        },
        isElasticStatementDisabled() {
            return this.editingMode?.isBlock || this.isGroupedLimitSelected || !this.isFlexLimitTimeTypeActive;
        },
        isRequestLimitDisabled() {
            return this.editingMode?.isBlock || this.isGroupedLimitSelected || !this.isFlexLimitTimeTypeActive;
        },
        isTurnOffDisabled() {
            return this.editingMode?.isBlock || this.isGroupedLimitSelected;
        },
        isSoundNotificationDisabled() {
            return this.editingMode?.isBlock || this.isGroupedLimitSelected;
        },
        isSaveDisabled() {
            return this.isSaving || !this.editingMode || this.editingMode.typeId <= 0 || !this.hasActiveLimitTimeType;
        }
    },
    mounted() {
        this.resetForm();
    },
    methods: {
        resetForm() {
            this.editingMode = {
                id: this.mode.id,
                type: this.mode.type,
                typeId: Number(this.mode.typeId),
                isBlock: !!this.mode.isBlock,
                firstStatement: this.mode.firstStatement,
                secondStatement: this.mode.secondStatement,
                thirdStatement: this.mode.thirdStatement,
                nextStatement: this.mode.nextStatement,
                limitTimeType: Number(this.mode.limitTimeType) || 1,
                limitGroupId: this.mode.limitGroupId ?? this.mode.id,
                elasticStatement: this.mode.elasticStatement ?? this.defaultElasticStatement,
                requestLimitId: this.mode.requestLimitId ?? this.defaultRequestLimitId,
                turnOff: !!this.mode.turnOff,
                isSoundNotification: !!this.mode.isSoundNotification
            };

            this.normalizeExclusiveFields();
        },
        syncEditedTypeName() {
            if (!this.editingMode) {
                return;
            }

            const selectedType = this.statementTypes.find((type) => type.id === this.editingMode.typeId);
            this.editingMode.type = selectedType?.name || "";
        },
        setLimitTimeType(typeId, isEnabled) {
            if (!this.editingMode) {
                return;
            }

            if (isEnabled) {
                this.editingMode.limitTimeType = typeId;
                return;
            }
            else {
                if (typeId === 1) {
                    this.editingMode.limitTimeType = 2;
                }
                else if (typeId === 2) {
                    this.editingMode.limitTimeType = 1;
                }
            }

            if (Number(this.editingMode.limitTimeType) === typeId) {
                this.editingMode.limitTimeType = null;
            }
        },
        handleElasticStatementChange() {
            if (!this.editingMode) {
                return;
            }

            this.editingMode.elasticStatement = this.normalizeNumber(this.editingMode.elasticStatement, this.defaultElasticStatement);
        },
        handleRequestLimitChange() {
            if (!this.editingMode) {
                return;
            }

            this.editingMode.requestLimitId = this.normalizeNumber(this.editingMode.requestLimitId, this.defaultRequestLimitId);
        },
        normalizeExclusiveFields() {
            if (!this.editingMode) {
                return;
            }

            this.editingMode.elasticStatement = this.normalizeNumber(this.editingMode.elasticStatement, this.defaultElasticStatement);
            this.editingMode.requestLimitId = this.normalizeNumber(this.editingMode.requestLimitId, this.defaultRequestLimitId);
        },
        normalizeNumber(value) {
            const parsed = Number(value);

            if (!Number.isFinite(parsed)) {
                return 0;
            }

            return parsed;
        },
        normalizeNullableNumber(value) {
            if (value === null || value === undefined || value === "") {
                return null;
            }

            const parsed = Number(value);

            return Number.isFinite(parsed) ? parsed : null;
        },
        closeModal() {
            this.$emit("close");
        },
        async saveMode() {
            if (this.isSaveDisabled) {
                return;
            }

            try {
                this.isSaving = true;

                await DiscussionProfileService.UpdateMode(this.discussionId, this.editingMode.id, {
                    id: this.editingMode.id,
                    type: this.editingMode.type,
                    typeId: this.editingMode.typeId,
                    isBlock: this.editingMode.isBlock,
                    firstStatement: this.normalizeNumber(this.editingMode.firstStatement),
                    secondStatement: this.normalizeNumber(this.editingMode.secondStatement),
                    thirdStatement: this.normalizeNumber(this.editingMode.thirdStatement),
                    nextStatement: this.normalizeNumber(this.editingMode.nextStatement),
                    limitTimeType: this.normalizeNumber(this.editingMode.limitTimeType) || 1,
                    limitGroupId: this.normalizeNullableNumber(this.editingMode.limitGroupId),
                    elasticStatement: Number(this.editingMode.limitTimeType) === 2 ? this.normalizeNumber(this.editingMode.elasticStatement) : 0,
                    requestLimitId: this.normalizeNullableNumber(this.editingMode.requestLimitId),
                    turnOff: this.editingMode.turnOff,
                    isSoundNotification: this.editingMode.isSoundNotification
                });

                this.$emit("updated");
                this.closeModal();
            } catch (e) {
                if (e.response !== undefined) {
                    const t = this.$swal.mixin(AlertHelper.errorToastElement());
                    t.fire(
                        AlertHelper.errorToastAlert(
                            this.$t("com.global.default-save-error"),
                            this.$t("com.global.error")
                        )
                    );
                }
            } finally {
                this.isSaving = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.discussion-profile-mode-form {
    display: flex;
    flex-direction: column;
    row-gap: 16px;

    &__row {
        display: flex;
        flex-direction: column;
    }

    &__switch-row {
        display: flex;
        align-items: center;
        column-gap: 12px;
        min-height: 38px;
    }

    &__switch-label {
        margin: 0;
    }

    &__type-groups {
        display: flex;
        gap: 16px;
        margin-top: 8px;
        align-items: flex-start;
    }

    &__type-group {
        flex: 1 1 280px;
        display: flex;
        flex-direction: column;
        row-gap: 8px;
        padding: 12px;
        border: 1px solid #d1d6df;
        border-radius: 4px;
        background: #ffffff;
    }

    &__type-group--disabled {
        background: #f3f4f6;
        border-color: #d8dce5;
    }

    &__group-message {
        padding: 12px 16px;
        border: 1px solid #d8dce5;
        border-radius: 4px;
        background: #f3f4f6;
        color: #5c6377;
        font-size: 14px;
        line-height: 1.45;
    }

    &__option-switches {
        display: flex;
        flex-direction: column;
        row-gap: 8px;
        margin-top: 8px;
    }
}

.edit-point-form__input {
    width: 100%;
    padding: 12.5px 16px;
    background: #ffffff;
    border: 1px solid #d1d6df;
    box-sizing: border-box;
    box-shadow: 1px 1px 3px rgba(0, 8, 44, 0.06);
    border-radius: 4px;
    margin: 8px 0px;
    font-size: 16px;
    line-height: 144%;
    color: #00082c;

    &:focus {
        border: 1px solid #00082c;
    }
}

.edit-point-form__label {
    font-weight: 600;
    font-size: 14px;
    line-height: 145%;
    color: #00082c;
}
</style>
